import React, { useMemo, useState } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import type { SelectablePlayer } from '../../types/match';

interface MatchSquadPickerProps {
  /** A korosztály játékosai, akik közül a keret választható. */
  players: SelectablePlayer[];
  selected: Set<number>;
  onChange: (selected: Set<number>) => void;
}

/**
 * A mérkőzés kerete már a létrehozáskor: a kijelöltek jelezhetnek vissza,
 * a korosztály többi tagja látja a meccset és értesítést is kap róla.
 */
const MatchSquadPicker: React.FC<MatchSquadPickerProps> = ({ players, selected, onChange }) => {
  const [search, setSearch] = useState('');

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return term ? players.filter((p) => p.name.toLowerCase().includes(term)) : players;
  }, [players, search]);

  const allVisibleSelected = visible.length > 0 && visible.every((p) => selected.has(p.id));

  const toggle = (id: number) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next);
  };

  const toggleAll = () => {
    const next = new Set(selected);
    if (allVisibleSelected) visible.forEach((p) => next.delete(p.id));
    else visible.forEach((p) => next.add(p.id));
    onChange(next);
  };

  if (players.length === 0) {
    return <p className="text-sm text-gray-500">A kiválasztott korosztályban nincs aktív játékos.</p>;
  }

  return (
    <div className="space-y-2">
      <div className="relative">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Keresés név alapján..."
          aria-label="Keresés a játékosok között"
          className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-base focus:outline-none focus:ring-2 focus:ring-primary-500 sm:text-sm"
        />
      </div>
      <div className="rounded-md border border-gray-200">
        <label className="flex items-center gap-2 border-b border-gray-200 px-3 py-2 text-xs font-medium text-gray-600">
          <input
            type="checkbox"
            checked={allVisibleSelected}
            onChange={toggleAll}
            disabled={visible.length === 0}
            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          Összes kijelölése ({visible.length}) · {selected.size} kiválasztva
        </label>
        <ul className="max-h-56 divide-y divide-gray-100 overflow-y-auto">
          {visible.map((p) => (
            <li key={p.id}>
              <label className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-gray-900 hover:bg-gray-50">
                <input
                  type="checkbox"
                  checked={selected.has(p.id)}
                  onChange={() => toggle(p.id)}
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                {p.name}
              </label>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default MatchSquadPicker;
